import { useState } from 'react';
import { F, EASE, DUR } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCurrency } from '../context/CurrencyContext';
import { useT } from '../i18n/useT';
import { fmt, fmtC } from '../utils/format';
import { getTxLabel, getTxColor } from '../utils/transactionTypes';
import { coinFullName, buildTxView, txTypeTag, TRANSFER_PAIR_TAG } from '../utils/txView';
import CoinImage from './CoinImage';
import Private from './Private';
import ExchangeLogo from './ExchangeLogo';
import { Icon, Icons } from './red/Icons';
import { usePress } from './red/Press';
import { COIN_MAP } from '../utils/coingecko';
import ReorderHandle from './ReorderHandle';

// ── Helpers ──────────────────────────────────────────────────────────────────

// t/locale optional — wenn nicht übergeben, fällt die Funktion auf Deutsch
// zurück (Verhalten für Nicht-React-Aufrufer unverändert).
export function formatDateHeader(dateStr, t = null, locale = 'de-DE') {
  if (!dateStr || dateStr === 'Unbekannt') return t ? t('tx_unknown') : 'Unbekannt';
  const d    = new Date(dateStr + 'T12:00:00Z');
  const now  = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return t ? t('today') : 'Heute';
  if (diff === 1) return t ? t('tx_yesterday') : 'Gestern';
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

// Reihenfolge innerhalb eines Tages: Eingänge zuerst, dann Tausch, dann Ausgänge.
// Wenn beide TX `zeit` haben, schlägt die echte Uhrzeit die Heuristik.
const TYPE_ORDER = {
  kauf: 0, einzahlung: 1,
  staking: 2, airdrop: 2, mining: 2, cashback: 2, interest: 2,
  swap: 3,
  auszahlung: 4, verkauf: 5, burn: 5,
};

export function groupByDate(txs) {
  const groups = {};
  for (const tx of txs) {
    const key = tx.datum || 'Unbekannt';
    if (!groups[key]) groups[key] = [];
    groups[key].push(tx);
  }
  for (const key in groups) {
    groups[key].sort((a, b) => {
      if (a.zeit && b.zeit && a.zeit !== b.zeit) return b.zeit < a.zeit ? -1 : 1; // DESC: neuere oben
      return (TYPE_ORDER[b.typ] ?? 9) - (TYPE_ORDER[a.typ] ?? 9); // DESC: Ausgänge oben
    });
  }
  return Object.entries(groups).sort(([a], [b]) => new Date(b) - new Date(a));
}

// Global Chains + Datumsgruppierung in einem Pass.
// Eine Chain wird im Tages-Bucket der einzahlung (späteren TX) eingeordnet,
// damit USDC-Transfer 18.12. → 19.12. als 1 Karte im 19.12.-Bucket erscheint.
// Rückgabe: [ [dateKey, items[]], ... ] absteigend sortiert,
// items = { type: 'chain', txs: [ausTx, einTx] } | { type: 'single', txs: [tx] }
export function buildGroupedChains(txs, transferMap) {
  const txById = new Map(txs.map(t => [t._docId || String(t.id), t]));
  const inChain = new Set();
  const pairs = [];

  for (const tx of txs) {
    const id = tx._docId || String(tx.id);
    if (inChain.has(id)) continue;
    const linkedId = tx.linkedTxId || transferMap?.get(id)?.linkedTxId;
    if (!linkedId) continue;
    const partner = txById.get(linkedId);
    if (!partner) continue;
    const partnerId = partner._docId || String(partner.id);
    if (inChain.has(partnerId)) continue;

    const ausTx = tx.typ === 'auszahlung' ? tx : partner;
    const einTx = tx.typ === 'einzahlung' ? tx : partner;
    // nur echte Paare auszahlung↔einzahlung
    if (!(ausTx.typ === 'auszahlung' && einTx.typ === 'einzahlung')) continue;

    pairs.push({ ausTx, einTx });
    inChain.add(id);
    inChain.add(partnerId);
  }

  const groups = {};
  for (const tx of txs) {
    const id = tx._docId || String(tx.id);
    if (inChain.has(id)) continue;
    const key = tx.datum || 'Unbekannt';
    (groups[key] ||= []).push({ type: 'single', txs: [tx] });
  }
  for (const { ausTx, einTx } of pairs) {
    const chainDate = einTx.datum || ausTx.datum || 'Unbekannt';
    (groups[chainDate] ||= []).push({ type: 'chain', txs: [ausTx, einTx] });
  }

  // Innerhalb eines Tages DESC sortieren — neueste oben, älteste unten.
  // manualOrderIndex hat Vorrang (manuelles Reordering, 0 = oben), sonst zeit + TYPE_ORDER DESC.
  const itemTx = (item) => item.type === 'chain' ? item.txs[1] : item.txs[0];
  for (const key in groups) {
    groups[key].sort((a, b) => {
      const tA = itemTx(a), tB = itemTx(b);
      const oA = tA.manualOrderIndex ?? Infinity;
      const oB = tB.manualOrderIndex ?? Infinity;
      if (oA !== Infinity || oB !== Infinity) return oA - oB;
      if (tA.zeit && tB.zeit && tA.zeit !== tB.zeit) return tB.zeit < tA.zeit ? -1 : 1; // DESC
      return (TYPE_ORDER[tB.typ] ?? 9) - (TYPE_ORDER[tA.typ] ?? 9); // DESC: Ausgänge oben
    });
  }

  return Object.entries(groups).sort(([a], [b]) => new Date(b) - new Date(a));
}

// ── TransferChainCard ─────────────────────────────────────────────────────────
// txA = auszahlung (gesendet), txB = einzahlung (empfangen)

// Press-fähiger Sub-Action-Button (Bearbeiten/Löschen): scale-only Squish,
// kein Glow/Border/Color-Wechsel. Die KARTE selbst ist wischbar (SwipeAction) →
// nur diese kleinen Buttons bekommen Press.
function SubActionBtn({ style, onClick, children }) {
  const { pressTransform, pressHandlers } = usePress({ scale: 0.96 });
  return (
    <button
      {...pressHandlers}
      onClick={onClick}
      style={{
        ...style,
        transform: pressTransform,
        transition: `transform ${DUR.press}ms ${EASE}`,
      }}
    >
      {children}
    </button>
  );
}

export function TransferChainCard({ txA, txB, marketData, transferMap, derivedValues, onEdit, onDelete, onUnlink, flat }) {
  const { T } = useTheme();
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  // Einzel-Bein-Modus (kein txB): externer Versand/Empfang ohne erfasstes
  // Gegenstück. Route („Ledger → Externe Wallet") + Tages-EUR-Wert kommen aus
  // buildTxView — exakt dieselbe Logik wie die normale Transfer-Zeile. Der
  // Paar-Modus (txA+txB) bleibt unverändert.
  const single    = !txB;
  const view       = single ? buildTxView(txA, { transferMap, derivedValues, marketData, locale }) : null;
  const isOut      = txA.typ === 'auszahlung';
  // Typ-Identität (Farbe + Glyph) aus der EINEN Wahrheit: Einzel-Bein → txTypeTag
  // (gesendet/empfangen), echtes Paar → TRANSFER_PAIR_TAG (gold). Keine eigenen
  // Farben mehr — die Badge spiegelt exakt die Feed-Plakette.
  const tag        = single ? txTypeTag(view) : TRANSFER_PAIR_TAG;

  const coin      = txA.coin || txB?.coin || '';
  const menge     = txA.menge || 0;
  const fromEx    = single ? (view?.flow?.fromName || txA.exchange || '—') : (txA.exchange || '—');
  const toEx      = single ? (view?.flow?.toName || 'Externe Wallet')     : (txB.exchange || '—');
  const feeTotal  = single ? (txA.gebuehr || 0) : ((txA.gebuehr || 0) + (txB.gebuehr || 0));
  const isManual  = single ? false : !!(txA.manualLink || txB.manualLink);

  const subBtn = (red) => ({
    flex: 1, padding: '10px 0', background: 'none', border: 'none',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    cursor: 'pointer', fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em',
    color: red ? T.red : T.text, fontFamily: F.family,
    WebkitTapHighlightColor: 'transparent',
  });

  return (
    <div style={{
      background: flat ? 'transparent' : T.surface,
      border: flat ? 'none' : `1px solid ${T.line || T.border}`,
      borderBottom: flat ? `1px solid ${T.lineSoft || T.line}` : `1px solid ${T.line || T.border}`,
      borderRadius: flat ? 0 : 3, overflow: 'hidden',
      boxShadow: 'none',
    }}>
      {/* Summary row — Calm: Token · „X transferiert" · A → B · Menge · interner Transfer */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: flat ? '14px 4px 14px 10px' : '13px 14px',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 14,
          position: 'relative',
        }}
      >
        {/* Transfer-Marker (Ansatz A): gefülltes 20px-Badge mit Transfer-Pfeil
            (zwei gegenläufig) unten rechts — markiert die Zeile klar als Transfer
            (und damit als aufklappbares Paar), bündigkeits-neutral. */}
        <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
          <CoinImage sym={coin} marketData={marketData} size={flat ? 38 : 36} />
          <span style={{
            position: 'absolute', right: -3, bottom: -3,
            width: 20, height: 20, borderRadius: '50%',
            // Farbe + Glyph aus der zentralen Typ-Plakette (txTypeTag): gold
            // „Transfer" (Paar), orange „Gesendet", blau „Empfangen".
            background: tag.color,
            border: `2px solid ${T.bg}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d={tag.iconPath} />
            </svg>
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Headline = farbiger Typ-Schriftzug (Gesendet/Empfangen/Transfer) —
              gleiche Sprache wie TxRow. Coin über Logo + Menge + Aufklappen. */}
          <div style={{
            fontFamily: F.family, fontSize: 14.5, fontWeight: 700,
            letterSpacing: '-0.01em', color: tag.color,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {tag.label}
          </div>
          <div style={{
            fontFamily: F.family, fontSize: 12.5, color: T.sub, marginTop: 3,
            letterSpacing: '-0.005em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromEx}</span>
            <span style={{ color: T.mute }}>→</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toEx}</span>
            {isManual && <span style={{ color: T.mute }}>· {t('tx_linked_manually')}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
          <div style={{
            fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
            fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em', color: T.text,
          }}>
            <Private>{fmtC(menge)}</Private> {coin}
          </div>
          <div style={{ fontFamily: F.family, fontSize: 12.5, color: T.sub, marginTop: 3 }}>
            {single
              ? (Number(view?.displayValueEur) > 0
                  ? <>{view.valueIsDerived && <span style={{ color: T.mute }}>~ </span>}<Private>{`${fmt(convert(view.displayValueEur), locale)} ${symbol}`}</Private></>
                  : feeTotal > 0
                    ? <>{t('tx_fee_short')} <Private>{`${fmt(convert(feeTotal), locale)} ${symbol}`}</Private></>
                    : null)
              : feeTotal > 0
                ? <>{t('tx_fee_short')} <Private>{`${fmt(convert(feeTotal), locale)} ${symbol}`}</Private></>
                : t('tx_internal_transfer')}
          </div>
        </div>
        {/* Aufklapp-Chevron — rechtsbündig nach dem Betrag, identisch zu TxRow
            (alle TX-Karten gleicher Stil). Kein zentrierter Desktop-Pfeil mehr. */}
        <span aria-hidden style={{
          flexShrink: 0, marginLeft: 2, display: 'inline-flex', color: T.mute,
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: `transform ${DUR.fast}ms ${EASE}`,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </span>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${T.lineSoft || T.line}` }}>

          {/* Interner Eigen-Transfer (Paar): steuerneutral-Hinweis */}
          {!single && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', fontSize: 11.5, color: T.sub, background: T.surfaceHi || T.surface, fontFamily: F.family }}>
              {tag.iconPath && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={tag.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={tag.iconPath} /></svg>}
              {t('tx_internal_transfer')}
            </div>
          )}

          {/* Von / An — externe Adresse (gesendet/empfangen) bzw. beide eigenen
              Wallets (Paar). Eigene Beine sind direkt editier-/löschbar. */}
          {(() => {
            const short = (a) => { const s = String(a || '').trim(); return s.length > 16 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s; };
            const copy = (e, a) => { e.stopPropagation(); try { navigator.clipboard?.writeText(String(a || '')); } catch { /* noop */ } };
            let legs;
            if (single && isOut) {
              legs = [
                { dir: t('tx_from'), own: txA,  name: txA.exchange, addr: txA.chainFrom, ext: false },
                { dir: t('tx_to'),   own: null, name: view?.flow?.toName || t('tx_external_short'), addr: txA.chainTo, ext: true },
              ];
            } else if (single) {
              legs = [
                { dir: t('tx_from'), own: null, name: view?.flow?.fromName || t('tx_external_short'), addr: txA.chainFrom, ext: true },
                { dir: t('tx_to'),   own: txA,  name: txA.exchange, addr: txA.chainTo, ext: false },
              ];
            } else {
              legs = [
                { dir: t('tx_from'), own: txA, name: txA.exchange, addr: txA.chainFrom, ext: false },
                { dir: t('tx_to'),   own: txB, name: txB.exchange, addr: txB.chainTo, ext: false },
              ];
            }
            return legs.map((leg, i) => (
              <div key={i} style={{ padding: '10px 14px', borderTop: i === 0 ? 'none' : `1px solid ${T.lineSoft || T.line}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: tag.color, fontFamily: F.family }}>{leg.dir}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.muted, fontFamily: F.family }}>
                    {leg.ext ? leg.name : <><ExchangeLogo name={leg.name} size={14} />{leg.name}</>}
                    {!leg.ext && !single && <span style={{ color: T.mute }}>· {t('tx_own_wallet_suffix')}</span>}
                  </span>
                </div>
                {leg.own && (
                  <div style={{ fontSize: 12, color: T.text, fontFamily: F.family, marginTop: 5 }}>
                    <Private>{fmtC(leg.own.menge)}</Private> {coin}
                    {leg.own.gebuehr > 0 && <span style={{ color: T.muted }}> · {t('tx_fee_short')} <Private>{`${fmt(convert(leg.own.gebuehr), locale)} ${symbol}`}</Private></span>}
                  </div>
                )}
                {leg.addr && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                    <span onClick={(e) => copy(e, leg.addr)} title={leg.addr} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: T.sub, background: T.surfaceHi || T.surface, border: `1px solid ${T.lineSoft || T.line}`, borderRadius: 7, padding: '3px 7px', cursor: 'pointer' }}>{short(leg.addr)}</span>
                  </div>
                )}
                {leg.own?.hash && (
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 4, fontFamily: F.family }}>#{String(leg.own.hash).slice(0, 14)}…</div>
                )}
                {leg.own && (
                  <div style={{ display: 'flex', marginTop: 8, borderTop: `1px solid ${T.lineSoft || T.line}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, paddingTop: 8 }}>
                    <SubActionBtn onClick={e => { e.stopPropagation(); onEdit(leg.own); }} style={subBtn(false)}>{t('tx_edit')}</SubActionBtn>
                    <SubActionBtn onClick={e => { e.stopPropagation(); onDelete(leg.own.id || leg.own._docId); }} style={subBtn(true)}>{t('tx_delete')}</SubActionBtn>
                  </div>
                )}
              </div>
            ));
          })()}

          {/* Transfer trennen (nur manuell verbundene) */}
          {isManual && onUnlink && (
            <div style={{
              padding: '11px 14px',
              borderTop: `1px solid ${T.glassBorder}`,
            }}>
              {!confirmUnlink ? (
                <SubActionBtn
                  onClick={e => { e.stopPropagation(); setConfirmUnlink(true); }}
                  style={{
                    width: '100%', padding: '9px 0', background: 'none',
                    border: `1px solid ${T.glassBorder}`, borderRadius: 10,
                    cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                    color: T.muted, fontFamily: F.family,
                  }}
                >{t('tx_unlink_transfer')}</SubActionBtn>
              ) : (
                <div>
                  <div style={{
                    fontSize: 11.5, color: T.muted, fontFamily: F.family,
                    textAlign: 'center', marginBottom: 9, lineHeight: 1.4,
                  }}>
                    {t('tx_unlink_confirm')}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <SubActionBtn
                      onClick={e => { e.stopPropagation(); setConfirmUnlink(false); }}
                      style={{
                        flex: 1, padding: '9px 0', background: 'none',
                        border: `1px solid ${T.glassBorder}`, borderRadius: 10,
                        cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                        color: T.muted, fontFamily: F.family,
                      }}
                    >{t('tx_cancel')}</SubActionBtn>
                    <SubActionBtn
                      onClick={e => { e.stopPropagation(); setConfirmUnlink(false); onUnlink(txA, txB); }}
                      style={{
                        flex: 1, padding: '9px 0',
                        background: 'linear-gradient(180deg, #C2453F 0%, #B33E3A 100%)',
                        border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10,
                        cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                        color: '#fff', fontFamily: F.family,
                      }}
                    >{t('tx_unlink_confirm_yes')}</SubActionBtn>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ── TxCard ────────────────────────────────────────────────────────────────────

const SUGGESTION_COLOR = { strong: '#2ECC71', medium: '#F5A623', weak: '#E2814B' };

export function TxCard({
  tx, isOpen, onToggle, onEdit, onDelete, transferMap, marketData, linkedTx,
  selectable = false, selected = false, onSelectToggle, suggestion = null, dimmed = false,
  selectVariant = 'link', // 'link' (blue) | 'delete' (red)
  reorderable = false, isDragging = false, dragControls,
}) {
  const { T } = useTheme();
  const { isWide } = useBreakpoint(); void isWide;
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const isSwap        = tx.typ === 'swap';
  const isTransferIn  = tx.typ === 'einzahlung';
  const isTransferOut = tx.typ === 'auszahlung';
  const isTransfer    = isTransferIn || isTransferOut;

  const typColor = getTxColor(tx.typ);
  const typLabel = getTxLabel(tx.typ);

  const matchInfo   = isTransfer ? (transferMap?.get(tx._docId || String(tx.id))) : null;
  const txMatched   = matchInfo?.matchStatus === 'matched';
  const txUnmatched = matchInfo?.matchStatus === 'unmatched';
  const transferBadge = isTransferIn ? `↓ ${t('tx_inflow')}` : `↑ ${t('tx_outflow')}`;

  const sym  = tx.coin || tx.coinOut || '';
  const meta = COIN_MAP[sym?.toUpperCase()];

  // ── Futures-PnL / Funding-Fee: eigene Karte mit Pink-Akzent ─────────────
  const isFuturesPnl = tx.typ === 'futures_pnl' || tx.typ === 'funding_fee';
  if (isFuturesPnl) {
    return (
      <FuturesPnlCard
        tx={tx}
        isOpen={isOpen}
        onToggle={onToggle}
        onEdit={onEdit}
        onDelete={onDelete}
        marketData={marketData}
        selectable={selectable}
        selected={selected}
        onSelectToggle={onSelectToggle}
        reorderable={reorderable}
        isDragging={isDragging}
        dragControls={dragControls}
      />
    );
  }

  // Transfer-Flow (wie in WalletScreen)
  const transferPartner = matchInfo?.matchedPlatform;
  const externalLabel = tx.externalTransfer ? t('tx_external_wallet') : null;
  const transferFromEx = isTransferIn ? (transferPartner || externalLabel || '?') : (tx.exchange || '—');
  const transferToEx   = isTransferIn ? (tx.exchange || '—') : (transferPartner || externalLabel || '?');

  // Auswahl-Farbe je nach Variante (Verbinden=blau, Löschen=rot)
  const SEL_SOLID  = selectVariant === 'delete' ? '#E4544A' : '#60A5FA';
  const SEL_BG     = selectVariant === 'delete' ? 'rgba(228,84,74,0.10)' : 'rgba(96,165,250,0.10)';
  const SEL_BORDER = selectVariant === 'delete' ? 'rgba(228,84,74,0.85)' : 'rgba(96,165,250,0.85)';
  return (
    <div style={{
      background: selected ? SEL_BG : T.surface,
      border: `1px solid ${selected ? SEL_BORDER : (isOpen ? T.line : T.line)}`,
      borderLeft: txUnmatched && !selected && !isOpen
        ? '3px solid #F5A623'
        : undefined,
      borderRadius: 3, overflow: 'hidden',
      boxShadow: isDragging ? T.cardShadowLg : 'none',
      opacity: dimmed ? 0.45 : 1,
      transform: isDragging ? 'scale(1.03)' : 'scale(1)',
      zIndex: isDragging ? 10 : 'auto',
      transition: isDragging ? 'none' : `border-color ${DUR.base}ms ease, box-shadow ${DUR.base}ms ease, opacity ${DUR.base}ms ease, transform ${DUR.base}ms ease`,
    }}>
      {/* Main row */}
      <div
        onClick={selectable ? () => onSelectToggle?.() : onToggle}
        style={{ padding: '13px 14px', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>

          {/* Drag Handle (Reorder-Modus) */}
          <ReorderHandle visible={reorderable} dragControls={dragControls} />

          {/* Auswahl-Checkbox (Verbinden-/Löschen-Modus) */}
          {selectable && (
            <div style={{
              width: 22, height: 22, borderRadius: 7, flexShrink: 0,
              background: selected ? SEL_SOLID : 'transparent',
              border: `1.5px solid ${selected ? SEL_SOLID : T.glassBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: `background ${DUR.fast}ms ease, border-color ${DUR.fast}ms ease`,
            }}>
              {selected && (
                <Icon d={Icons.check} size={13} stroke="#fff" sw={3} />
              )}
            </div>
          )}

          {/* Icon */}
          {isSwap ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <CoinImage sym={tx.coinOut} marketData={marketData} size={26} />
              <span style={{ color: T.muted, fontSize: 11 }}>→</span>
              <CoinImage sym={tx.coinIn}  marketData={marketData} size={26} />
            </div>
          ) : (
            <CoinImage sym={sym} marketData={marketData} size={38} />
          )}

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 3,
            }}>
              {isTransfer ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 14, fontWeight: 700, color: T.text, fontFamily: F.family,
                  minWidth: 0, overflow: 'hidden',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
                    {transferFromEx}
                  </span>
                  <svg width={14} height={9} viewBox="0 0 26 10" fill="none"
                    stroke="#60A5FA" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
                    style={{ flexShrink: 0 }}>
                    <path d="M1 5h24M19 1l6 4-6 4"/>
                  </svg>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
                    {transferToEx}
                  </span>
                </span>
              ) : (
                <span style={{
                  fontSize: 14, fontWeight: 700, color: T.text,
                  fontFamily: F.family, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {isSwap ? `${tx.coinOut} → ${tx.coinIn}` : (meta?.name || sym)}
                </span>
              )}
              {!isTransfer && tx.preis > 0 && (
                <span style={{
                  fontSize: 14, fontWeight: 700, color: T.text,
                  fontFamily: F.family, flexShrink: 0, marginLeft: 8,
                }}>
                  {fmt(convert(tx.preis), locale)} {symbol}
                </span>
              )}
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.muted, fontFamily: F.family, flexWrap: 'wrap' }}>
                <ExchangeLogo name={tx.exchange} size={14} />{tx.exchange}
                {tx.wallet && (
                  <>
                    <svg width={10} height={6} viewBox="0 0 26 10" fill="none"
                      stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
                      style={{ flexShrink: 0, opacity: 0.6 }}>
                      <path d="M1 5h24M19 1l6 4-6 4"/>
                    </svg>
                    <ExchangeLogo name={tx.wallet} size={14} />
                    <span>{t('tx_wallet_suffix', { name: tx.wallet })}</span>
                  </>
                )}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, fontFamily: F.family,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: isTransfer ? '#60A5FA' : typColor,
              }}>
                {isTransfer ? transferBadge : typLabel}
              </span>
            </div>

            {/* Menge */}
            {isSwap ? (
              <div style={{ fontSize: 11, color: T.muted, fontFamily: F.family, marginTop: 2 }}>
                <Private>{String(tx.mengeOut)}</Private> {tx.coinOut} → <Private>{String(tx.mengeIn)}</Private> {tx.coinIn}
              </div>
            ) : tx.menge > 0 && (
              <div style={{ fontSize: 11, color: T.muted, fontFamily: F.family, marginTop: 2 }}>
                <Private>{fmtC(tx.menge)}</Private> {sym}
              </div>
            )}

            {/* Transfer-Partner info — Datum des Gegenstücks */}
            {isTransfer && txMatched && matchInfo.matchedDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                <span style={{ fontSize: 10, color: T.muted, fontFamily: F.family }}>{t('tx_counterpart', { date: matchInfo.matchedDate })}</span>
              </div>
            )}
            {suggestion && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4,
                background: 'rgba(96,165,250,0.12)',
                border: '1px solid rgba(96,165,250,0.3)',
                borderRadius: 20, padding: '2px 8px',
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: SUGGESTION_COLOR[suggestion.quality] || SUGGESTION_COLOR.weak,
                }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: '#60A5FA', fontFamily: F.family }}>
                  {t('tx_suggested', { score: suggestion.score })}
                </span>
              </div>
            )}
            {!suggestion && isTransfer && txUnmatched && !tx.externalTransfer && (
              <div style={{ fontSize: 10, color: '#F5A623', fontFamily: F.family, marginTop: 3 }}>
                {t('tx_no_counterpart')}
              </div>
            )}
          </div>
        </div>

        {/* Tags row */}
        {(tx.gebuehr > 0 || tx.feesIncludedInSpread || tx.hash || tx.notiz) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
            {tx.gebuehr > 0 && (
              <span style={{
                fontSize: 11, color: T.muted,
                background: T.glassMid,
                border: `1px solid ${T.glassBorder}`,
                padding: '2px 8px', borderRadius: 20, fontFamily: F.family,
              }}>
                {t('tx_fee')}: <Private>{`${fmt(convert(tx.gebuehr), locale)} ${symbol}`}</Private>
              </span>
            )}
            {tx.feesIncludedInSpread && !tx.gebuehr && (
              <span
                title={t('tx_fee_spread_tip')}
                style={{
                  fontSize: 11, color: T.muted,
                  background: T.glassMid,
                  border: `1px solid ${T.glassBorder}`,
                  padding: '2px 8px', borderRadius: 20, fontFamily: F.family,
                  cursor: 'help',
                }}>
                {t('tx_fee_in_spread')}
              </span>
            )}
            {tx.hash && (
              <span style={{
                fontSize: 11, color: T.muted,
                background: T.glassMid,
                border: `1px solid ${T.glassBorder}`,
                padding: '2px 8px', borderRadius: 20, fontFamily: F.family,
              }}>
                #{tx.hash.slice(0, 8)}…
              </span>
            )}
            {tx.blockchainVerified && (
              <span style={{
                fontSize: 11, color: '#2ECC71',
                background: 'rgba(46,204,113,0.10)',
                border: '1px solid rgba(46,204,113,0.22)',
                padding: '2px 8px', borderRadius: 20, fontFamily: F.family,
              }}>
                ⛓ {t('tx_onchain')}
              </span>
            )}
            {tx.notiz && tx.notiz !== 'CSV Import' && (
              <span style={{
                fontSize: 11, color: T.muted,
                background: T.glassMid,
                border: `1px solid ${T.glassBorder}`,
                padding: '2px 8px', borderRadius: 20, fontFamily: F.family,
              }}>
                {tx.notiz}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Verknüpfte TX (expanded detail) */}
      {isOpen && linkedTx && (
        <div style={{
          padding: '10px 14px',
          borderTop: `1px solid rgba(96,165,250,0.1)`,
          background: 'rgba(96,165,250,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{
                fontSize: 10, color: '#60A5FA', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.08em',
                marginBottom: 3, fontFamily: F.family,
              }}>
                {isTransferIn ? `↑ ${t('tx_sent_from')}` : `↓ ${t('tx_received_on')}`}
              </div>
              <div style={{ fontSize: 12, color: '#93C5FD', fontFamily: F.family, fontWeight: 600 }}>
                <Private>{fmtC(linkedTx.menge)}</Private> {linkedTx.coin}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.muted, fontFamily: F.family }}>
                <ExchangeLogo name={linkedTx.exchange} size={14} />{linkedTx.exchange}
              </div>
              <div style={{ fontSize: 10, color: T.muted, fontFamily: F.family, marginTop: 2 }}>{linkedTx.datum}</div>
            </div>
          </div>
          {tx.transferIntent && (
            <div style={{ fontSize: 10, color: 'rgba(96,165,250,0.6)', marginTop: 4, fontFamily: F.family }}>
              {tx.transferIntent === 'self' ? t('tx_own_wallet') : t('tx_external_wallet')}
            </div>
          )}
        </div>
      )}

      {/* Action strip */}
      {isOpen && (
        <div style={{ display: 'flex', borderTop: `1px solid rgba(255,255,255,0.07)` }}>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(tx); }}
            style={{
              flex: 1, padding: '11px 0', background: 'none', border: 'none',
              borderRight: `1px solid ${T.border}`, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family,
            }}
          >{t('tx_edit')}</button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(tx.id || tx._docId); }}
            style={{
              flex: 1, padding: '11px 0', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: T.red, fontFamily: F.family,
            }}
          >{t('tx_delete')}</button>
        </div>
      )}
    </div>
  );
}

// ── FuturesPnlCard ───────────────────────────────────────────────────────────
// Dedizierte Karte fuer typ='futures_pnl' (Bitget USDT-M Futures Liquidations
// / Position-Closes) und typ='funding_fee'. Pink-Akzent (#F472B6), signed PnL
// mit +/-, Position-Side (Long/Short), Liquidation-Badge.
function FuturesPnlCard({
  tx, isOpen, onToggle, onEdit, onDelete, marketData,
  selectable = false, selected = false, onSelectToggle,
  reorderable = false, isDragging = false, dragControls,
}) {
  const { T } = useTheme();
  const { isWide } = useBreakpoint(); void isWide;
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();

  const PINK = '#F472B6';
  const PINK_BG = 'rgba(244,114,182,0.10)';
  const PINK_BORDER = 'rgba(244,114,182,0.32)';

  const pnl       = Number(tx.preis || 0); // signed: <0=Verlust, >0=Gewinn
  const isLoss    = pnl < 0;
  const isGain    = pnl > 0;
  const isFunding = tx.typ === 'funding_fee';
  const sideLabel = tx.positionSide === 'long' ? 'Long'
                  : tx.positionSide === 'short' ? 'Short'
                  : null;
  const pairLabel = tx.futuresPair || null;
  const typLabel  = isFunding ? t('tx_funding_fee') : (tx.liquidation ? t('tx_liquidation') : t('tx_futures_pnl'));

  const pnlColor  = isLoss ? T.red : isGain ? T.green : T.muted;
  const pnlSign   = isGain ? '+' : isLoss ? '−' : '';
  const pnlAbs    = Math.abs(pnl);

  const SEL_BG     = 'rgba(96,165,250,0.10)';
  const SEL_BORDER = 'rgba(96,165,250,0.85)';

  return (
    <div style={{
      position: 'relative',
      background: selected ? SEL_BG : T.surface,
      border: `1px solid ${selected ? SEL_BORDER : (isOpen ? PINK_BORDER : T.line)}`,
      borderLeft: `3px solid ${PINK}`,
      borderRadius: 3, overflow: 'hidden',
      boxShadow: isDragging ? T.cardShadowLg : 'none',
      transform: isDragging ? 'scale(1.03)' : 'scale(1)',
      zIndex: isDragging ? 10 : 'auto',
      transition: isDragging ? 'none' : `border-color ${DUR.base}ms ease, box-shadow ${DUR.base}ms ease, transform ${DUR.base}ms ease`,
    }}>
      <div
        onClick={selectable ? () => onSelectToggle?.() : onToggle}
        style={{ padding: '13px 14px', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <ReorderHandle visible={reorderable} dragControls={dragControls} />
          {selectable && (
            <div style={{
              width: 22, height: 22, borderRadius: 7, flexShrink: 0,
              background: selected ? '#60A5FA' : 'transparent',
              border: `1.5px solid ${selected ? '#60A5FA' : T.glassBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {selected && (
                <Icon d={Icons.check} size={13} stroke="#fff" sw={3} />
              )}
            </div>
          )}

          {/* Pink Futures-Glyph statt Coin-Image */}
          <div style={{
            width: 38, height: 38, borderRadius: 12, flexShrink: 0,
            background: PINK_BG, border: `1px solid ${PINK_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: PINK,
          }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h4l3-7 4 14 3-7h4"/>
            </svg>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 3, gap: 8,
            }}>
              <span style={{
                fontSize: 14, fontWeight: 700, color: T.text,
                fontFamily: F.family, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                {pairLabel || (tx.coin || tx.sym || 'Futures')}
                {sideLabel && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.06em',
                    padding: '1px 6px', borderRadius: 4,
                    color: sideLabel === 'Long' ? T.green : T.red,
                    border: `1px solid ${sideLabel === 'Long' ? T.green : T.red}`,
                    textTransform: 'uppercase', fontFamily: F.family,
                  }}>{sideLabel}</span>
                )}
                {tx.liquidation && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.06em',
                    padding: '1px 6px', borderRadius: 4,
                    color: T.red, border: `1px solid ${T.red}`,
                    textTransform: 'uppercase', fontFamily: F.family,
                  }}>LIQ</span>
                )}
              </span>
              <span style={{
                fontSize: 15, fontWeight: 800, color: pnlColor,
                fontFamily: F.family, flexShrink: 0,
              }}>
                {pnlSign}<Private>{`${fmt(convert(pnlAbs), locale)} ${symbol}`}</Private>
              </span>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 11, color: T.muted, fontFamily: F.family,
              }}>
                <ExchangeLogo name={tx.exchange} size={14} />{tx.exchange}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, fontFamily: F.family,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: PINK,
              }}>
                {typLabel}
              </span>
            </div>

            {tx.menge > 0 && (
              <div style={{ fontSize: 11, color: T.muted, fontFamily: F.family, marginTop: 2 }}>
                <Private>{fmtC(tx.menge)}</Private> {tx.coin || tx.sym}
                {isLoss && ` ${t('tx_consumed')}`}
                {isGain && ` ${t('tx_gained')}`}
              </div>
            )}

            {tx.notiz && tx.notiz !== 'CSV Import' && (
              <div style={{ fontSize: 10, color: T.muted, fontFamily: F.family, marginTop: 3 }}>
                {tx.notiz}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action strip */}
      {isOpen && (
        <div style={{ display: 'flex', borderTop: `1px solid rgba(255,255,255,0.07)` }}>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(tx); }}
            style={{
              flex: 1, padding: '11px 0', background: 'none', border: 'none',
              borderRight: `1px solid ${T.border}`, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family,
            }}
          >{t('tx_edit')}</button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(tx.id || tx._docId); }}
            style={{
              flex: 1, padding: '11px 0', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: T.red, fontFamily: F.family,
            }}
          >{t('tx_delete')}</button>
        </div>
      )}
    </div>
  );
}
